<script setup>
import { vResizeObserver } from '@vueuse/components'
import { useElementVisibility } from '@vueuse/core'
import dayjs from 'dayjs'
import { debounce, isEmpty } from 'lodash'
import {
  computed,
  onMounted,
  ref,
  provide,
  nextTick,
  useTemplateRef,
  watch,
  onBeforeUnmount
} from 'vue'
import { useStore } from 'vuex'

import { ACTIONS_KEYS } from '@/utils/actions-keys'
import {
  CHECKIN_DATE_FORMAT,
  CHECKIN_DATE_FORMAT_SHORT,
  getCheckinNecessaryFieldsSet,
  userCanReadCheckin,
  userCanUpdateCheckin
} from '@/utils/check-ins/check-ins'
import { validateRepliesToShowCount } from '@/utils/comments'
import {
  AUTHOR_VARIANTS,
  CHECKIN_PREVIEW_ACTIONS_PLACEMENTS
} from '@/utils/components-configurations/checkin-field'
import { ACTION_TYPES } from '@/utils/components-configurations/chekin-author-actions'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { LIST_ITEM_TYPES } from '@/utils/components-configurations/okr-elements-list-item'
import { ASSIGNEE_ENTITY_KEYS, CHECKIN_ENTITY_KEYS, COMMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import { isStringEmpty } from '@/utils/general'
import { commentFormInjectionKey } from '@/utils/injection-keys'
import { currentUserCanReadObjective } from '@/utils/objectives'
import { includesValue } from '@/utils/prop-validators'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'
import { ELEMENT_VISIBILITY_CHECK_MODE } from '@/utils/window'

import { CHECKIN_PREVIEW_TEST_ID } from '@/components/check-ins/jest-helpers'
import CheckinAuthorActions from '@/components/objectives/element-check-in/CheckinAuthorActions'
import CheckinChangedParameterView from '@/components/objectives/element-check-in/CheckinChangedParameterView'
import CheckinFeedAuthor from '@/components/objectives/element-check-in/CheckinFeedAuthor'
import CheckinLayout from '@/components/objectives/element-check-in/CheckinLayout'
import CheckinField from '@/components/objectives/element-check-in/ChekinField'
import ContributeLine from '@/components/objectives/forms/ContributeLine'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import CommentItem from '@/components/objectives/forms/objective-form/CommentItem'
import CommentReplyButton from '@/components/objectives/forms/objective-form/CommentReplyButton'
import OkrElementsListItem from '@/components/objectives/okr-elements-list/OkrElementsListItem'
import AppReactions from '@/components/reactions/AppReactions'
import AppButton from '@/components/ui/AppButton/AppButton'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'

defineOptions({
  name: 'CheckinPreviewItem'
})

const props = defineProps({
  item: {
    type: Object,
    required: true
  },

  okrElement: {
    type: Object,
    default: () => ({})
  },

  showReplies: {
    type: Boolean
  },

  showActions: {
    type: Boolean
  },

  isVisibleOnScreen: {
    type: Boolean
  },

  watchItemVisibility: {
    type: Boolean
  },

  showAuthor: {
    type: Boolean,
    default: true
  },

  showElement: {
    type: Boolean
  },

  repliesToShow: {
    type: Number,
    default: Infinity,
    validator: v => validateRepliesToShowCount(v)
  },

  showRepliesConnections: {
    type: Boolean,
    default: true
  },

  authorVariant: {
    type: String,
    default: AUTHOR_VARIANTS.DEFAULT,
    validator: v => includesValue(v, AUTHOR_VARIANTS)
  },

  actionsPlacement: {
    type: String,
    default: CHECKIN_PREVIEW_ACTIONS_PLACEMENTS.HEADER,
    validator: v => includesValue(v, CHECKIN_PREVIEW_ACTIONS_PLACEMENTS)
  },

  autoScrollToFormMode: {
    type: String,
    default: ELEMENT_VISIBILITY_CHECK_MODE.SCROLLABLE_PARENT,
    validator: v => includesValue(v, ELEMENT_VISIBILITY_CHECK_MODE)
  },

  tableActionsAppendTo: {
    type: String,
    default: ''
  },

  showReactions: {
    type: Boolean
  },

  lazyLoadComments: {
    type: Boolean
  }
})

watch(
  () => props.isVisibleOnScreen,
  (newValue, oldValue) => {
    if (
      newValue === false &&
      oldValue === true &&
      props.watchItemVisibility &&
      replyFormRef.value
    ) {
      replyFormRef.value.closeReplyForm()
    }
  },
  {
    immediate: true
  }
)

const updatesLog = computed(() => {
  const resolvedFields = getCheckinNecessaryFieldsSet({
    typeId: props.okrElement[OKR_ELEMENT_KEYS.TYPE_ID],
    existContributeChildren: props.okrElement[OKR_ELEMENT_KEYS.EXIST_CONTRIBUTE_CHILDREN]
  })

  return Object.entries(resolvedFields).reduce((acc, [key, fieldParams]) => {
    if (!props.item[key]) {
      return acc
    }

    return [
      ...acc,
      {
        [CHECKIN_ENTITY_KEYS.ID]: key,
        [CHECKIN_ENTITY_KEYS.PERMISSIONS]: props.item[CHECKIN_ENTITY_KEYS.PERMISSIONS],
        ...fieldParams,
        ...(props.item[key] || {})
      }
    ]
  }, [])
})

const firstComment = computed(() => {
  if (isEmpty(props.item[CHECKIN_ENTITY_KEYS.COMMENTS])) {
    return null
  }
  return props.item[CHECKIN_ENTITY_KEYS.COMMENTS][0]
})

const isCommentEmpty = computed(() => {
  return !firstComment.value || isStringEmpty(firstComment.value[COMMENT_ENTITY_KEYS.TEXT])
})

const hasReadAccess = computed(() => {
  return userCanReadCheckin(props.item) && currentUserCanReadObjective(props.okrElement)
})

const hasUpdateAccess = computed(() => {
  return userCanUpdateCheckin(props.item)
})

// const canDelete = computed(() => {
//   return userCanDeleteCheckin(props.item)
// })

const dropdownMenuActions = computed(() => {
  const items = []

  if (hasUpdateAccess.value) {
    items.push({
      name: ACTIONS_KEYS.EDIT,
      title: 'action.edit',
      icon: 'edit-next'
    })
  }

  // if (canDelete.value) {
  //   items.push({
  //     name: ACTIONS_KEYS.REMOVE,
  //     title: 'action.delete',
  //     icon: 'delete-next',
  //     color: 'var(--grade-low-color-next)'
  //   })
  // }

  return items
})

const emit = defineEmits({
  'delete-checkin': null,
  'edit-checkin': null,
  'preview-resize': null,
  'update-replies': null,
  init: null,
  'edit-element': null,
  'update-element': null,
  'update-reactions': null
})

const isShowReplyButton = computed(() => {
  return hasReadAccess.value
})

const isShowReplyOnCheckinForm = ref(false)

const replyFormRef = useTemplateRef('comment-form-reference')

const layoutReference = useTemplateRef('layout-reference')

const onReplyButtonClick = () => {
  if (!isShowReplyButton.value) return
  isShowReplyOnCheckinForm.value = true

  nextTick(() => {
    replyFormRef.value.openReplyForm({
      ...firstComment.value,
      ...props.item[CHECKIN_ENTITY_KEYS.AUTHOR],
      [ASSIGNEE_ENTITY_KEYS.USER_NAME]:
        props.item[CHECKIN_ENTITY_KEYS.AUTHOR][ASSIGNEE_ENTITY_KEYS.NAME]
    })
  })

  if (props.watchItemVisibility) {
    setTimeout(() => {
      // replyFormRef.value.focusReplyForm()
      commitResize()
      layoutReference.value.scrollIntoView()
    })
  }
}

const isActionsExists = computed(() => {
  return !isEmpty(dropdownMenuActions.value) || isShowReplyButton.value
})

const isDropdownMenuOpened = ref(false)

const onMenuActionsClick = name => {
  if (name === ACTIONS_KEYS.REMOVE) {
    emit('delete-checkin', props.item)
  }

  if (name === ACTIONS_KEYS.EDIT) {
    emit('edit-checkin', props.item)
  }
}

onMounted(() => {
  emit('init')
})

const store = useStore()

const userData = computed(() => store.state.system.userData)

const targetIsVisible = props.lazyLoadComments ? useElementVisibility(layoutReference) : ref(true)

const wasTargetShown = ref(!props.lazyLoadComments)

let stop
const watchHandler = watch(targetIsVisible, newValue => {
  if (newValue) {
    wasTargetShown.value = true
    stop?.()
  }
})
stop = watchHandler.stop

onBeforeUnmount(() => {
  stop?.()
})

const shouldRenderComments = computed(() => {
  return props.lazyLoadComments ? wasTargetShown.value : true
})

const isRepliesShown = computed(() => {
  return (
    hasReadAccess.value && firstComment.value && props.showReplies && shouldRenderComments.value
  )
})

const isRepliesExists = computed(
  () => !isEmpty(firstComment.value[COMMENT_ENTITY_KEYS.CHILD_COMMENTS])
)

const isCommentShown = computed(() => {
  return (
    hasReadAccess.value && firstComment.value && !isCommentEmpty.value && shouldRenderComments.value
  )
})

const isContributeLineNeed = computed(() => {
  if (props.showRepliesConnections) {
    return (isRepliesShown.value && isRepliesExists.value) || isShowReplyOnCheckinForm.value
  }

  return false
})

const isCompactAuthorVariant = computed(() => props.authorVariant === AUTHOR_VARIANTS.COMPACT)

const shouldShowActions = computed(() => props.showActions && isActionsExists.value)

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ COMMENT FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const commentFormData = ref({
  opened: undefined // undefined if no comment form is opened and uid of component if it is
})

provide(commentFormInjectionKey, commentFormData)

const updateRepliesList = ({ comments }) => {
  const currentComment = comments.find(
    comment => comment[COMMENT_ENTITY_KEYS.ID] === firstComment.value[COMMENT_ENTITY_KEYS.ID]
  )

  if (!currentComment) return

  emit('update-replies', currentComment[COMMENT_ENTITY_KEYS.CHILD_COMMENTS] || [])
}

const onUpdateReply = ({ commentId, text }) => {
  const newReplies = firstComment.value[COMMENT_ENTITY_KEYS.CHILD_COMMENTS].map(comment => {
    if (comment[COMMENT_ENTITY_KEYS.ID] === commentId) {
      return {
        ...comment,
        [COMMENT_ENTITY_KEYS.TEXT]: text
      }
    }
    return comment
  })

  updateRepliesList({
    comments: [
      {
        [COMMENT_ENTITY_KEYS.ID]: firstComment.value[COMMENT_ENTITY_KEYS.ID],
        [COMMENT_ENTITY_KEYS.CHILD_COMMENTS]: newReplies
      }
    ]
  })
}

const onUpdateReactions = ({ commentId, parentId, reactions }) => {
  emit('update-reactions', {
    checkinId: props.item[CHECKIN_ENTITY_KEYS.ID],
    commentId,
    parentId,
    reactions
  })
}

const onReplyRemoved = ({ id }) => {
  const newReplies = firstComment.value[COMMENT_ENTITY_KEYS.CHILD_COMMENTS].filter(
    comment => comment[COMMENT_ENTITY_KEYS.ID] !== id
  )

  updateRepliesList({
    comments: [
      {
        [COMMENT_ENTITY_KEYS.ID]: firstComment.value[COMMENT_ENTITY_KEYS.ID],
        [COMMENT_ENTITY_KEYS.CHILD_COMMENTS]: newReplies
      }
    ]
  })
}

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ COMMENT FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const commitResize = () => {
  emit('preview-resize')
}

const debouncedCommitResize = debounce(() => {
  commitResize()
}, 300)

const onPreviewResize = () => {
  debouncedCommitResize()
}
</script>

<template>
  <CheckinLayout
    ref="layout-reference"
    v-resize-observer="onPreviewResize"
    :data-auto-testid="CHECKIN_PREVIEW_TEST_ID"
    :data-testid="CHECKIN_PREVIEW_TEST_ID"
    :divied="isCompactAuthorVariant"
    class="cpi-Item"
  >
    <template v-if="showAuthor" #author>
      <div class="cpi-Header">
        <CheckinAuthorActions
          v-if="authorVariant === AUTHOR_VARIANTS.DEFAULT"
          :action-type="ACTION_TYPES.READ"
          :user-data="item[CHECKIN_ENTITY_KEYS.AUTHOR]"
        >
          <template #date>
            <div class="cpi-Header_Date">
              {{ dayjs(item[CHECKIN_ENTITY_KEYS.CHECKIN_DATE]).format(CHECKIN_DATE_FORMAT) }}
            </div>
          </template>
        </CheckinAuthorActions>

        <CheckinFeedAuthor
          v-if="isCompactAuthorVariant"
          :user-data="item[CHECKIN_ENTITY_KEYS.AUTHOR]"
        >
          <template #date>
            {{ dayjs(item[CHECKIN_ENTITY_KEYS.CHECKIN_DATE]).format(CHECKIN_DATE_FORMAT_SHORT) }}
          </template>
        </CheckinFeedAuthor>

        <div
          v-if="shouldShowActions && actionsPlacement === CHECKIN_PREVIEW_ACTIONS_PLACEMENTS.HEADER"
          :class="{
            'cpi-Header_Actions-active-within': isDropdownMenuOpened
          }"
          class="cpi-Header_Actions"
        >
          <CommentReplyButton v-if="isShowReplyButton" @click="onReplyButtonClick" />

          <div v-if="!isEmpty(dropdownMenuActions)">
            <DropdownMenu
              :items="dropdownMenuActions"
              :offset="[0, 0]"
              :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
              preferred-position="bottom-end"
              @close="isDropdownMenuOpened = false"
              @open="isDropdownMenuOpened = true"
              @item-click="onMenuActionsClick"
            >
              <template #activator="{ isOpened }">
                <AppButton
                  :class="{
                    'cpi-MenuTrigger-active': isOpened
                  }"
                  icon="more-next"
                  size="sm"
                  type="subtle"
                />
              </template>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </template>

    <template #body>
      <OkrElementsListItem
        v-if="showElement"
        :allowed-depth="0"
        :objective="okrElement"
        :show-avatar="false"
        :show-grade="false"
        :type="LIST_ITEM_TYPES.PRIMARY_NEXT"
        hide-actions
        no-border
        show-groups
        show-interval
        @edit-element="emit('edit-element', $event)"
      />

      <div class="cpi-BodyFields">
        <ContributeLine
          v-if="isContributeLineNeed"
          :class="{
            'cpi-BodyFields_ContributeLine-extended': isShowReplyOnCheckinForm && !isRepliesExists
          }"
          class="cpi-BodyFields_ContributeLine"
        />
        <CheckinField
          v-for="logItem in updatesLog"
          :key="logItem[CHECKIN_ENTITY_KEYS.ID]"
          :field="logItem"
        >
          <template #field-value="{ field }">
            <CheckinChangedParameterView :okr-element :parameter="field" />
          </template>
        </CheckinField>

        <DescriptionField
          v-if="isCommentShown"
          :model-value="firstComment[COMMENT_ENTITY_KEYS.TEXT]"
          :show-placeholder="false"
          :stlyed="false"
          :workspace-id="okrElement[OKR_ELEMENT_KEYS.WORKSPACE_ID]"
          class="cpi-Comment"
          readonly
          simple-view
        />

        <div
          :class="{
            'cpi-BodyActions-has-next-siblings': isRepliesShown && isRepliesExists,
            [`cpi-BodyActions-placement-${actionsPlacement}`]:
              actionsPlacement === CHECKIN_PREVIEW_ACTIONS_PLACEMENTS.AFTER_COMMENT
          }"
          class="cpi-BodyActions"
        >
          <CommentReplyButton
            v-if="
              shouldShowActions &&
              isShowReplyButton &&
              actionsPlacement === CHECKIN_PREVIEW_ACTIONS_PLACEMENTS.AFTER_COMMENT
            "
            @click="onReplyButtonClick"
          />
          <AppReactions
            v-if="showReactions && firstComment"
            :comment-id="firstComment.id"
            :reactions="firstComment.reactions"
            :readonly="!showActions"
            @update-reactions="
              onUpdateReactions({
                parentId: firstComment[COMMENT_ENTITY_KEYS.PARENT_ID],
                commentId: firstComment[COMMENT_ENTITY_KEYS.ID],
                reactions: $event
              })
            "
          />
        </div>
      </div>
      <CommentItem
        v-if="isRepliesShown"
        ref="comment-form-reference"
        :auto-scroll-to-form-mode
        :hide-actions="!showActions"
        :hide-reply-button="!showActions"
        :item="firstComment"
        :objective="okrElement"
        :readonly-reactions="!showActions"
        :replies-to-show="repliesToShow"
        :show-replies-connections="showRepliesConnections"
        :table-actions-append-to
        :user-data
        can-be-replied
        show-only-replies
        @remove="onReplyRemoved"
        @create-reply="updateRepliesList"
        @update-reply="onUpdateReply"
        @update-reactions="onUpdateReactions($event)"
        @reply-form-open="isShowReplyOnCheckinForm = true"
        @reply-form-close="isShowReplyOnCheckinForm = false"
      />
    </template>
  </CheckinLayout>
</template>

<style lang="scss" scoped>
.cpi-Item {
  --app-droplist-box-shadow: initial;
  --replies-list-mx: 0;
  --replies-list-gap: 16px;
  --replies-list-my: var(--replies-list-gap) 0;
  --description-field-margin-bottom: 0;
  --checkin-layout-body-gap: 0;
  --oeli-item-padding: 24px 0;
  --oeli-item-hover-bg-color: transparent;
  --oeli-groups-list-width: 220px;
  --oeli-groups-list-justify-content: flex-end;
  --oeli-interval-font-weight: #{fw('medium')};
  --body-fields-gap: 4px;
}

.cpi-Header_Date {
  flex-shrink: 0;
}

.cpi-Header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cpi-Comment {
  --description-field-margin-bottom: 0;
  margin-top: 12px;
  --comment-item-wrapper-gap: 0;
}

.cpi-Header_Actions {
  &:not(.cpi-Header_Actions-active-within):not(.cpi-Item:hover &) {
    display: none;
  }

  display: flex;
  gap: 8px;
}

.cpi-MenuTrigger-active {
  background-color: $dark-2;
  color: $white;
}

.cpi-BodyFields {
  display: inherit;
  flex-direction: inherit;
  gap: var(--body-fields-gap, 0px);
  position: relative;
}

.cpi-BodyFields_ContributeLine {
  position: absolute;
  left: calc(0px - var(--fields-padding-left) + 11px);
  top: 0;

  --additional-offset: 0px;

  &-extended {
    --additional-offset: 8px;
  }

  --height: calc(100% + 15px + var(--additional-offset) + var(--replies-list-gap));
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector
%border-styles {
  content: '';
  display: block;
  width: calc(100% + var(--checkin-layout-pl, 0) + var(--checkin-layout-pr, 0));
  height: 1px;
  background-color: $grey-3-next;
  position: absolute;
  left: calc(var(--checkin-layout-pl, 0) * -1);
}

.cpi-BodyActions {
  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;

  &-placement-after-comment {
    --body-actions-py: 16px;
    padding-top: var(--body-actions-py);
    margin-top: calc(24px - var(--body-fields-gap));
  }

  &-placement-after-comment#{&}-has-next-siblings {
    margin-bottom: calc(24px - var(--replies-list-gap));
    padding-bottom: var(--body-actions-py);

    &:after {
      bottom: 0;
      @extend %border-styles;
    }
  }

  &:before {
    @extend %border-styles;
    top: 0;
  }
}
</style>
